/**
 * Grid de "Indicadores duros" — tercera tarjeta de Capa 2.
 *
 * Renderiza 7 métricas atómicas que el funcionario consulta de un
 * vistazo: captación total, per cápita, población, # de proyectos,
 * sector fuerte/débil (derivados del breakdown sectorial) y MDM score.
 *
 * El sub-componente `Indicator` se queda co-localizado porque solo se
 * usa desde este componente y comparte la convención visual.
 *
 * `score` puede ser null (falló /score o aún no está precalculado): en
 * ese caso las métricas que salen del score muestran "—" y las del
 * profile (población, MDM) se pintan igual (#559).
 */

import { Banknote, Coins, FileText, Gauge, TrendingDown, TrendingUp, Users } from "lucide-react";

import { type MunicipalityScoreDTO } from "@/lib/api";
import type { MunicipalityProfileDTO } from "@/lib/types";

import { bigIntFromCop, formatCop } from "./_shared";

export function HardIndicators({
  score,
  profile,
}: {
  score: MunicipalityScoreDTO | null;
  profile: MunicipalityProfileDTO;
}) {
  // MDM puede venir en el profile como indicador (dict indicator_key → valor)
  const mdm = profile.indicators?.["mdm_score_total"];

  // Sector fuerte / débil — derivados del detalle sectorial del
  // score (max / min por `score`, excluyendo entries con score null).
  // Datos atómicos para los indicadores; el detalle expandible vive
  // en Capa 1.
  const scoredSectors = (score?.sectorial_scores ?? []).filter((s) => s.score !== null);
  const sortedByScore = [...scoredSectors].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const strongest = sortedByScore[0] ?? null;
  const weakest = sortedByScore.length > 1 ? sortedByScore[sortedByScore.length - 1] : null;

  // Captación total — formato corto B/MM/M/K para que quepa en card.
  // Sin score no es "$0" (eso afirmaría que no captó nada): es "—".
  const captacionCop = score ? bigIntFromCop(score.captacion_total_cop) : 0n;
  const captacionLabel = captacionCop > 0n ? formatCop(captacionCop) : score ? "$0" : "—";

  // Captación per cápita — derivada. Es la métrica que nivela munis
  // grandes vs chicos (un muni cat 6 con $1MM y 5k habitantes capta
  // PROPORCIONALMENTE más que una capital con $1MM y 500k).
  // Solo calculable si tenemos población y captación > 0.
  const population = profile.population ?? 0;
  const perCapitaCop =
    captacionCop > 0n && population > 0 ? captacionCop / BigInt(population) : null;
  const perCapitaLabel = perCapitaCop !== null ? formatCop(perCapitaCop) : "—";

  // Población formateada con separador de miles
  const populationLabel = population > 0 ? population.toLocaleString("es-CO") : "—";

  return (
    <div
      data-testid="ficha-hard-indicators"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs md:col-span-2"
    >
      <h2 className="text-sm font-bold text-slate-900">Indicadores</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        Métricas clave de captación y desempeño sectorial.
      </p>
      {/* Quitamos Categoría y PDET del grid — ya viven en el header
          de arriba para no duplicar metadata. Esta grid se enfoca en
          los numbers que importan para la decisión. */}
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        <Indicator
          icon={
            <Banknote
              className={`h-4 w-4 ${captacionCop > 0n ? "text-emerald-600" : "text-slate-400"}`}
              aria-hidden="true"
            />
          }
          label="Captación SGR"
          value={captacionLabel}
        />
        <Indicator
          icon={
            <Coins
              className={`h-4 w-4 ${perCapitaCop !== null ? "text-emerald-600" : "text-slate-400"}`}
              aria-hidden="true"
            />
          }
          label="Per cápita"
          value={perCapitaLabel}
          subValue={perCapitaCop !== null ? "por habitante" : undefined}
        />
        <Indicator
          icon={<Users className="h-4 w-4 text-slate-400" aria-hidden="true" />}
          label="Población"
          value={populationLabel}
          subValue={population > 0 ? "habitantes" : undefined}
        />
        <Indicator
          icon={<FileText className="h-4 w-4 text-slate-400" aria-hidden="true" />}
          label="Proyectos SGR"
          value={score ? score.project_count_total.toLocaleString("es-CO") : "—"}
        />
        <Indicator
          icon={
            <TrendingUp
              className={`h-4 w-4 ${
                strongest && (strongest.score ?? 0) >= 1.1 ? "text-emerald-600" : "text-slate-400"
              }`}
              aria-hidden="true"
            />
          }
          label="Sector fuerte"
          value={strongest ? `${(strongest.score ?? 0).toFixed(2)}x` : "—"}
          subValue={strongest ? strongest.sector.toLowerCase() : undefined}
        />
        <Indicator
          icon={
            <TrendingDown
              className={`h-4 w-4 ${
                weakest && (weakest.score ?? 0) < 0.9 ? "text-red-600" : "text-slate-400"
              }`}
              aria-hidden="true"
            />
          }
          label="Sector débil"
          value={weakest ? `${(weakest.score ?? 0).toFixed(2)}x` : "—"}
          subValue={weakest ? weakest.sector.toLowerCase() : undefined}
        />
        <Indicator
          icon={<Gauge className="h-4 w-4 text-slate-400" aria-hidden="true" />}
          label="MDM score"
          value={mdm ? Number(mdm.value).toFixed(1) : "—"}
        />
      </div>
    </div>
  );
}

/**
 * Indicador atómico: icono + label corto + valor grande + sub-línea
 * opcional (ej. nombre del sector cuando el valor es un score).
 */
function Indicator({
  icon,
  label,
  value,
  valueColor,
  subValue,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | number;
  valueColor?: string;
  /** Línea secundaria opcional (ej. nombre del sector). */
  subValue?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-[10px] font-semibold tracking-wider text-slate-500 uppercase">{label}</p>
      </div>
      <p className={`mt-1 text-lg font-bold tabular-nums ${valueColor ?? "text-slate-900"}`}>
        {value}
      </p>
      {subValue && (
        <p className="mt-0.5 truncate text-[11px] text-slate-500 capitalize" title={subValue}>
          {subValue}
        </p>
      )}
    </div>
  );
}
